import { useState } from "react";
import { FileCode, Radio } from "lucide-react";
import Modal from "@/components/common/Modal";
import Button from "@/components/common/Button";
import { useApp } from "@/contexts/AppContext";

const SOURCES = [
  {
    id: "vast",
    label: "VAST tag",
    description: "Import from a VAST XML ad tag URL.",
    Icon: FileCode,
    placeholder: "https://…/vast.xml",
  },
  {
    id: "rtb",
    label: "RTB response",
    description: "Coming soon.",
    Icon: Radio,
    placeholder: "https://…/bid",
    disabled: true,
  },
];

export default function ImportCampaignModal({ isOpen, onClose, onImported }) {
  const { performRequest } = useApp();
  const [type, setType] = useState(null);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setType(null);
    setUrl("");
    setError(null);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleImport = async () => {
    if (!type || !url.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await performRequest("/campaigns/import", {
        method: "POST",
        body: JSON.stringify({ type, url: url.trim() }),
      });
      onImported(data);
      reset();
    } catch (err) {
      console.error("Failed to import campaign", err);
      setError(err.message || "Failed to import from the provided URL.");
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <>
      <Button variant="secondary" onClick={handleClose} disabled={loading}>
        Cancel
      </Button>
      <Button onClick={handleImport} disabled={loading || !type || !url.trim()}>
        {loading ? "Importing…" : "Import"}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import campaign"
      size="medium"
      footer={footer}
    >
      <div className="flex flex-col gap-5">
        <div>
          <p className="mb-2 text-sm font-medium text-(--text-primary)">
            Source
          </p>
          <div className="grid grid-cols-2 gap-3">
            {SOURCES.map((source) => {
              const isActive = type === source.id;
              const { Icon } = source;
              return (
                <button
                  key={source.id}
                  type="button"
                  disabled={source.disabled}
                  onClick={() => setType(source.id)}
                  className={`flex flex-col items-start gap-1 rounded-(--radius-md) border px-4 py-3 text-left transition-colors ${
                    source.disabled
                      ? "cursor-not-allowed border-(--border-subtle) bg-(--surface-2) opacity-50"
                      : isActive
                        ? "border-(--brand-primary) bg-(--brand-primary)/5"
                        : "border-(--border-subtle) bg-(--surface-2) hover:bg-(--surface-muted)"
                  }`}
                >
                  <Icon
                    size={18}
                    className={
                      isActive
                        ? "text-(--brand-primary)"
                        : "text-(--text-secondary)"
                    }
                  />
                  <span className="text-sm font-semibold text-(--text-primary)">
                    {source.label}
                  </span>
                  <span className="text-xs text-(--text-secondary)">
                    {source.description}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="form-group-modal">
          <label htmlFor="import-url">
            {type === "rtb" ? "RTB response URL" : "VAST tag URL"}
          </label>
          <input
            id="import-url"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder={
              SOURCES.find((s) => s.id === type)?.placeholder ?? "https://…"
            }
            disabled={!type}
          />
          <p className="form-help-text">
            We&apos;ll fetch the URL and autofill the basics and creative
            details, then take you to the audience step.
          </p>
        </div>

        {error && <p className="text-sm text-(--status-danger)">{error}</p>}
      </div>
    </Modal>
  );
}
